/**
 * TradeConfirmationPopup — shows trade execution details with real-time data
 */
import { useEffect, useState } from 'react';

interface Props {
  symbol: string;
  side: 'BUY' | 'SELL';
  quantity: number;
  price: number;
  timestamp?: number;
  notional?: number;
  isVisible: boolean;
  onClose?: () => void;
}

export function TradeConfirmationPopup({
  symbol,
  side,
  quantity,
  price,
  timestamp = Date.now(),
  notional = quantity * price,
  isVisible,
  onClose,
}: Props) {
  const [show, setShow] = useState(isVisible);

  useEffect(() => {
    setShow(isVisible);
    if (isVisible) {
      const timer = setTimeout(() => {
        setShow(false);
        onClose?.();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isVisible, onClose]);

  if (!show) return null;

  const time = new Date(timestamp).toLocaleTimeString();
  const date = new Date(timestamp).toLocaleDateString();

  return (
    <div
      style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        zIndex: 10000,
        animation: 'popupSlideIn 0.3s ease-out',
      }}
    >
      <div
        style={{
          backgroundColor: 'var(--dark-bg)',
          border: `2px solid ${side === 'BUY' ? 'var(--green)' : 'var(--red)'}`,
          borderRadius: 8,
          padding: 24,
          boxShadow: `0 10px 40px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1)`,
          minWidth: 320,
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <div
            style={{
              fontSize: 32,
              fontWeight: 700,
              color: side === 'BUY' ? 'var(--green)' : 'var(--red)',
              marginBottom: 8,
            }}
          >
            {side} ORDER EXECUTED
          </div>
          <div style={{ fontSize: 24, fontWeight: 600, marginBottom: 4 }}>{symbol}</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {date} {time}
          </div>
        </div>

        <div style={{ marginBottom: 16, fontSize: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, paddingBottom: 8, borderBottom: '1px solid var(--border)' }}>
            <span style={{ color: 'var(--text-muted)' }}>Symbol</span>
            <span style={{ fontWeight: 600 }}>{symbol}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ color: 'var(--text-muted)' }}>Side</span>
            <span style={{ fontWeight: 600, color: side === 'BUY' ? 'var(--green)' : 'var(--red)' }}>
              {side}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ color: 'var(--text-muted)' }}>Quantity</span>
            <span style={{ fontWeight: 600 }}>{quantity.toLocaleString()}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ color: 'var(--text-muted)' }}>Price</span>
            <span style={{ fontWeight: 600 }}>{price.toFixed(2)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 8, borderTop: '1px solid var(--border)' }}>
            <span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>Notional</span>
            <span style={{ fontWeight: 700, fontSize: 16, color: side === 'BUY' ? 'var(--green)' : 'var(--red)' }}>
              {notional.toLocaleString('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 })}
            </span>
          </div>
        </div>

        <div
          style={{
            padding: 12,
            backgroundColor: 'rgba(255, 107, 53, 0.1)',
            borderRadius: 4,
            fontSize: 11,
            textAlign: 'center',
            color: '#ff6b35',
            fontWeight: 600,
            marginBottom: 8,
            border: '1px solid rgba(255, 107, 53, 0.3)',
          }}
        >
          ⚠️ DEMO ORDER — No real money charged
        </div>

        <div
          style={{
            padding: 12,
            backgroundColor: side === 'BUY' ? 'rgba(0, 255, 0, 0.05)' : 'rgba(255, 0, 0, 0.05)',
            borderRadius: 4,
            fontSize: 12,
            textAlign: 'center',
            color: 'var(--text-muted)',
          }}
        >
          Order confirmed and added to Live Trades
        </div>
      </div>

      <style>{`
        @keyframes popupSlideIn {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
      `}</style>
    </div>
  );
}
